import React, { useState } from 'react';
import PostcodeSearch from './PostcodeSearch';
import './StepOne.scss'; // Import the new SCSS file

function PropertyStepOne({ propertyDetails, handleChange, handleNext }) {
  const [selectedAddress, setSelectedAddress] = useState(
    propertyDetails.address || ''
  );
  const [errors, setErrors] = useState({}); // Object to track errors for each field

  // Validate the postcode
  const validatePostcode = (postcode) => {
    const postcodeRegex = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i;
    return postcodeRegex.test(postcode.trim());
  };

  // Check if all required fields are filled and valid
  const isFormValid = () => {
    const isValid =
      validatePostcode(propertyDetails.postcode) &&
      selectedAddress &&
      propertyDetails.area &&
      propertyDetails.bedrooms &&
      propertyDetails.propertyType &&
      propertyDetails.isRental !== null;
    console.log('Form Validity:', isValid);
    return isValid;
  };

  const handleNextClick = () => {
    const newErrors = {};

    // Validate postcode in StepOne
    if (!validatePostcode(propertyDetails.postcode)) {
      newErrors.postcode = true;
      console.log('Postcode invalid');
    }

    // Validate address only if postcode is valid but no address is selected
    if (validatePostcode(propertyDetails.postcode) && !selectedAddress) {
      newErrors.address = true;
      console.log('Postcode valid, but no address selected');
    }

    // Continue validation for other fields...
    if (!propertyDetails.area) {
      newErrors.area = true;
      console.log('Area is required');
    }
    if (!propertyDetails.bedrooms) {
      newErrors.bedrooms = true;
      console.log('Bedrooms are required');
    }
    if (!propertyDetails.propertyType) {
      newErrors.propertyType = true;
      console.log('Property Type is required');
    }
    if (propertyDetails.isRental === null) {
      newErrors.isRental = true;
      console.log('Rental/Sales selection is required');
    }

    setErrors(newErrors);
    console.log('Validation Errors:', newErrors);

    if (Object.keys(newErrors).length === 0) {
      handleNext();
    }
  };

  const handlePostcodeChange = (postcode) => {
    console.log('Postcode changed:', postcode);
    // Update the postcode in the state
    const trimmedPostcode = postcode.trim();
    handleChange({ target: { name: 'postcode', value: trimmedPostcode } });

    // Validate the postcode and set the error state if it's invalid
    if (!validatePostcode(postcode)) {
      setErrors((prevErrors) => ({ ...prevErrors, postcode: true }));
      console.log('Postcode is invalid');
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, postcode: false }));
      console.log('Postcode is valid');
    }
  };

  // Handle address selection
  const handleAddressSelect = (address) => {
    console.log('Address selected:', address);
    setSelectedAddress(address);
    handleChange({ target: { name: 'address', value: address } });
    if (errors.address) {
      setErrors((prevErrors) => ({ ...prevErrors, address: false }));
    }
  };

  // Handle other input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    handleChange(e);
    console.log(`${name} changed:`, value);
    if (errors[name]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: false }));
    }
  };

  // Handle valuation type selection
  const handleValuationTypeChange = (isRentalValue) => {
    console.log('Valuation Type changed:', isRentalValue ? 'Rental' : 'Sales');
    handleChange({ target: { name: 'isRental', value: isRentalValue } });
    if (errors.isRental) {
      setErrors((prevErrors) => ({ ...prevErrors, isRental: false }));
    }
  };

  return (
    <div className="step-one-container">
      <h1 className="brand-title">Instant Property Valuation</h1>

      {/* Postcode Search component */}
      <div className="step-one-form-row">
        <PostcodeSearch
          onPostcodeChange={handlePostcodeChange}
          onSelectAddress={handleAddressSelect}
          postcode={propertyDetails.postcode}
          selectedAddress={selectedAddress}
          errors={errors} // Pass errors object to PostcodeSearch
        />
      </div>

      {/* Area selection */}
      <div className="step-one-form-row">
        <select
          name="area"
          value={propertyDetails.area}
          onChange={handleInputChange}
          className={`step-one-select ${errors.area ? 'error-border' : ''}`}
        >
          <option value="">Select Area</option>
          <option value="Chester Le Street">Chester Le Street</option>
          <option value="Consett">Consett</option>
          <option value="Crawcrook">Crawcrook</option>
          <option value="Durham">Durham</option>
          <option value="Houghton-le-Spring">Houghton-le-Spring</option>
          <option value="Lowfell">Lowfell</option>
          <option value="Rowlandsgill">Rowlandsgill</option>
          <option value="Sunderland">Sunderland</option>
          <option value="Wallsend">Wallsend</option>
          <option value="Washington">Washington</option>
          <option value="West Denton">West Denton</option>
          <option value="Whickham">Whickham</option>
          <option value="Winlaton">Winlaton</option>
          <option value="Whitley Bay">Whitley Bay</option>
          <option value="Other">Other</option>
        </select>
      </div>

      {/* Property type and bedrooms */}
      <div className="step-one-form-row property-type-bedrooms">
        <select
          name="propertyType"
          value={propertyDetails.propertyType}
          onChange={handleInputChange}
          className={`step-one-select ${errors.propertyType ? 'error-border' : ''}`}
        >
          <option value="">Select Type</option>
          <option value="terraced_house">Terraced House</option>
          <option value="semi-detached_house">Semi-Detached</option>
          <option value="detached_house">Detached</option>
          <option value="flat">Flat</option>
        </select>

        <select
          name="bedrooms"
          value={propertyDetails.bedrooms}
          onChange={handleInputChange}
          className={`step-one-select ${errors.bedrooms ? 'error-border' : ''}`}
        >
          <option value="">Select Bedrooms</option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="4">4</option>
          <option value="5">+5</option>
        </select>
      </div>

      {/* Valuation buttons */}
      <div
        className={`step-one-form-row step-one-valuation-buttons ${
          errors.isRental ? 'error-border' : ''
        }`}
      >
        <button
          type="button"
          className={`step-one-valuation-button ${
            propertyDetails.isRental === false ? 'active' : ''
          }`}
          onClick={() => handleValuationTypeChange(false)}
        >
          Sales Valuation
        </button>
        <button
          type="button"
          className={`step-one-valuation-button ${
            propertyDetails.isRental === true ? 'active' : ''
          }`}
          onClick={() => handleValuationTypeChange(true)}
        >
          Rental Valuation
        </button>
      </div>

      {/* Next button */}
      <div className="step-one-form-row">
        <button
          type="button"
          onClick={handleNextClick}
          className={`step-one-next-button ${!isFormValid() ? 'disabled-button' : ''}`}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default PropertyStepOne;
